import type { UserPlan } from "@/lib/auth/plans";
import { PLAN_MAX_RANGE_SEC } from "@/lib/auth/planLimits";

/**
 * When does a chart's "Sign in to see more history" / "Upgrade to Pro" banner
 * tell the truth?
 *
 * It promises older data. A game that has been tracked for four days has
 * none: the banner then sells an empty window, and someone who signs up for it
 * finds the same four days. The banner should show only when there plausibly
 * IS data before the start of what the viewer can see.
 *
 * Pure, clock-free: every timestamp comes from the chart (epoch seconds).
 */

/** How late a series may start and still count as filling its window. */
export const HISTORY_FILL_TOLERANCE = 0.2;
export const MIN_HISTORY_FILL_TOLERANCE_SEC = 6 * 60 * 60;

/**
 * Slack for a first point that sits a bucket before the release moment: a
 * bucketed point is stamped with its bucket's START. Windows a limited plan
 * can load (≤ 90 days) use buckets of 6 h at most.
 */
const RELEASE_SLACK_SEC = 6 * 60 * 60;

/**
 * True when data from at or before `windowStartTs` is plausible, i.e. the
 * series starts no later than the window start plus a tolerance. An unknown
 * start (`earliestTs` null) is plausible by definition — the banner keeps its
 * old behaviour when the chart can't say.
 */
export function hasOlderHistoryThan(
  earliestTs: number | null | undefined,
  windowStartTs: number,
  toleranceSec: number,
): boolean {
  if (earliestTs == null || !Number.isFinite(earliestTs)) return true;
  if (!Number.isFinite(windowStartTs)) return true;
  return earliestTs <= windowStartTs + Math.max(0, toleranceSec);
}

/** The tolerance for a window `widthSec` wide: a fifth of it, at least 6 h. */
export function historyFillToleranceSec(widthSec: number): number {
  if (!Number.isFinite(widthSec) || widthSec <= 0) return MIN_HISTORY_FILL_TOLERANCE_SEC;
  return Math.max(MIN_HISTORY_FILL_TOLERANCE_SEC, widthSec * HISTORY_FILL_TOLERANCE);
}

export type HistoryExtent = {
  /** The window the chart asked for, epoch seconds; `from` 0 = "all time". */
  from: number;
  to: number;
  /**
   * First MEANINGFUL point of what loaded, epoch seconds (a zero the CCU route
   * fills into an hour with no sample is not one); null when there is none.
   */
  firstPointTs: number | null;
  /**
   * How a late first point reads:
   * - "continuous": the series has a sample at every interval once tracking
   *   starts (followers, reviews), so a first point well inside the window IS
   *   where the data begins;
   * - "sparse": points exist only while a game is on a list (top sellers),
   *   when a price changes, or when anyone is playing (CCU stores non-zero
   *   samples only) — a late first point proves nothing on its own, and only
   *   `releaseTs` can bound the series.
   */
  kind: "continuous" | "sparse";
  /**
   * The release moment (epoch s) — used by sparse series only. A game can't
   * sell, play or change price on Steam long before it exists; pre-orders and
   * playtests show up as points BEFORE it, which this respects.
   */
  releaseTs?: number | null;
};

/**
 * Whether the chart may hide data the plan doesn't reach. ChartContainer
 * shows its history banner only when this is true (and the plan is limited).
 *
 * - The window the viewer actually got is `[max(from, to − plan limit), to]`.
 *   The server's `clampRange` floors at `now − plan limit` (a day earlier at
 *   most) for the clock-anchored charts, and at `to − plan limit` for a
 *   window ending at the game's release — the same start, give or take a day.
 * - A continuous series that starts well inside that window has nothing older.
 * - A sparse series with a past release and nothing loaded before it can't
 *   reach further back than the release: compare the release with the start
 *   of the plan's window.
 * - Anything else stays "plausible" — today's behaviour.
 */
export function mayHaveOlderHistory(extent: HistoryExtent, plan: UserPlan): boolean {
  const { from, to, firstPointTs, kind, releaseTs } = extent;
  const maxSec = PLAN_MAX_RANGE_SEC[plan];
  // Paid plans have no limit (the banner never shows for them anyway), and a
  // chart that hasn't read its clock yet (to = 0 during SSR) can't be judged.
  if (!Number.isFinite(maxSec) || !Number.isFinite(to) || to <= 0) return true;

  const planStart = to - maxSec;
  const loadedStart = Math.max(Number.isFinite(from) ? from : 0, planStart);

  if (kind === "continuous") {
    return hasOlderHistoryThan(firstPointTs, loadedStart, historyFillToleranceSec(to - loadedStart));
  }

  const release =
    releaseTs != null && Number.isFinite(releaseTs) && releaseTs <= to ? releaseTs : null;
  // The release bounds the series only when the loaded window REACHES it:
  // then "nothing loaded before the release" is evidence. A 48 h default
  // view of a game released weeks ago starts after the release by
  // construction, and an Early Access graduate (release_ts_utc = the 1.0
  // date) can have years of data before it — hiding the banner there sold
  // nothing and lost the upsell.
  if (
    release != null &&
    loadedStart <= release + RELEASE_SLACK_SEC &&
    (firstPointTs == null || firstPointTs >= release - RELEASE_SLACK_SEC)
  ) {
    return hasOlderHistoryThan(release, planStart, historyFillToleranceSec(maxSec));
  }
  return true;
}

/** Smallest finite timestamp among `values`, or null. */
export function earliestTs(values: Iterable<number | null | undefined>): number | null {
  let min: number | null = null;
  for (const v of values) {
    if (v == null || !Number.isFinite(v)) continue;
    if (min == null || v < min) min = v;
  }
  return min;
}
